<template>
  <div class="listening-test">
    <section v-if="phase === 'intro'" class="test-intro">
      <h1>{{ $t('levelTest.listeningName') }}</h1>
      <p class="muted">{{ $t('levelTest.listeningMeta') }}</p>
      <p class="intro-text">{{ $t('levelTest.listeningIntro') }}</p>
      <p class="test-range">{{ $t('levelTest.listeningRange') }}</p>
      <p v-if="failure" role="alert" class="audio-error">{{ failure }}</p>
      <button v-if="!user" type="button" class="primary-button" @click="showLoginModal">{{ $t('levelTest.login') }}</button>
      <button v-else type="button" class="primary-button" :disabled="starting" @click="start">{{ $t(starting ? 'levelTest.preparingTest' : 'levelTest.start') }}</button>
    </section>

    <section v-else-if="phase === 'question'" class="test-question">
      <p class="question-number">{{ $t('levelTest.listeningProgress', { n: state.answers.length + 1, total: items.length }) }} · {{ $t(item.type === 'dictation' ? 'levelTest.dictationType' : 'levelTest.comprehensionType') }}</p>
      <progress :value="state.answers.length" :max="items.length" :aria-label="$t('levelTest.testProgress')"></progress>
      <h1 class="question-text" :lang="item.type === 'choice' ? 'en' : undefined">{{ item.type === 'choice' ? item.question : $t('levelTest.dictationTitle') }}</h1>
      <p v-if="item.type === 'dictation'" id="dictation-hint" class="muted">{{ $t('levelTest.dictationHint') }}</p>
      <div class="player">
        <button type="button" class="play-button" :disabled="plays >= 2 || playing || loading" :aria-label="plays ? $t('levelTest.replay') : $t('levelTest.play')" aria-keyshortcuts="Space" :title="$t('levelTest.playShortcut')" @click="play">
          <span class="material-icons" aria-hidden="true">{{ playing ? 'graphic_eq' : plays ? 'replay' : 'play_arrow' }}</span>
        </button>
        <p class="muted" aria-live="polite">{{ loading ? $t('levelTest.audioLoading') : playing ? $t('levelTest.audioPlaying') : plays === 0 ? $t(item.type === 'dictation' ? 'levelTest.dictationPlayFirst' : 'levelTest.playFirst') : plays < 2 ? $t('levelTest.playsLeft', { n: 2 - plays }) : $t('levelTest.noPlaysLeft') }}</p>
        <p v-if="audioError" role="alert" class="audio-error">{{ $t('levelTest.audioFailed') }}</p>
      </div>
      <div v-if="item.type === 'choice'" class="options" role="group" :aria-label="item.question">
        <button v-for="(option, index) in options" :key="option" type="button" lang="en" :class="['option', { selected: draft === option }]" :aria-pressed="draft === option" :disabled="!listened || playing || loading" @click="draft = option">
          <span class="option-letter" aria-hidden="true">{{ String.fromCharCode(65 + index) }}</span>{{ option }}
        </button>
      </div>
      <div v-else class="dictation-editor">
        <label for="dictation-answer">{{ $t('levelTest.dictationAnswer') }}</label>
        <textarea id="dictation-answer" ref="dictationInput" v-model="draft" lang="en" rows="4" maxlength="500"
          :disabled="!plays" :placeholder="$t('levelTest.dictationPlaceholder')" aria-describedby="dictation-hint dictation-counter"
          spellcheck="false" autocomplete="off" autocorrect="off" autocapitalize="off"
          @keydown.ctrl.enter.prevent="submitTyped" @keydown.meta.enter.prevent="submitTyped"></textarea>
        <div id="dictation-counter" class="editor-footer"><span>{{ $t('levelTest.dictationShortcut') }}</span><span>{{ draft.length }} / 500</span></div>
      </div>
      <div class="question-actions">
        <button type="button" class="text-button" :disabled="!listened || playing || loading" @click="advance('')">{{ $t('levelTest.notHeard') }}</button>
        <button type="button" class="primary-button" :disabled="!draft.trim() || !listened || playing || loading" @click="advance(draft)">{{ $t(state.answers.length === items.length - 1 ? 'levelTest.seeResult' : 'levelTest.nextQuestion') }}</button>
      </div>
    </section>

    <section v-else-if="phase === 'scoring' || phase === 'assessmentError'" class="test-intro" aria-live="polite">
      <h1>{{ $t(phase === 'scoring' ? 'levelTest.listeningAssessing' : 'levelTest.listeningAssessmentFailed') }}</h1>
      <p class="intro-text">{{ phase === 'scoring' ? $t('levelTest.listeningAssessingHint') : failure }}</p>
      <div v-if="phase === 'assessmentError'" class="question-actions">
        <button v-if="!expired" type="button" class="primary-button" @click="assess">{{ $t('levelTest.retryAssessment') }}</button>
        <button type="button" class="text-button" @click="start">{{ $t('levelTest.restart') }}</button>
      </div>
    </section>

    <LevelResult v-else skill="listening" :level="result.level" :saved="saved" @retake="start">
      <dl class="result-metrics">
        <div><dt>{{ $t('levelTest.comprehensionType') }}</dt><dd>{{ result.choiceCorrect }} / {{ result.choiceTotal }}</dd></div>
        <div><dt>{{ $t('levelTest.dictationRecognition') }}</dt><dd>{{ result.dictationAccuracy }}%</dd></div>
      </dl>
      <AssessmentDetails><ListeningReview :result="result" /></AssessmentDetails>
    </LevelResult>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { apiPost } from '@/composables/useApi'
import { useAuth } from '@/composables/auth'
import { useModals } from '@/composables/modals'
import LevelResult from './LevelResult.vue'
import AssessmentDetails from './AssessmentDetails.vue'
import ListeningReview from './ListeningReview.vue'
import { useLearnerProfile } from '@/composables/useLearnerProfile'
import { readRecentQuestions, rememberQuestions, recordListeningAnswer, startListeningTest } from '@/utils/listeningTest'

function shuffle(list) {
  const result = [...list]
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(Math.random() * (index + 1))
    ;[result[index], result[swap]] = [result[swap], result[index]]
  }
  return result
}

export default {
  name: 'ListeningTest',
  components: { LevelResult, AssessmentDetails, ListeningReview },
  setup() {
    const { profile, saveProfile, completeOnboardingTest } = useLearnerProfile()
    const { user } = useAuth()
    const { showLoginModal } = useModals()
    return { profile, saveProfile, completeOnboardingTest, user, showLoginModal }
  },
  data() {
    return { items: [], attemptToken: '', phase: 'intro', state: null, options: [], draft: '', plays: 0,
      playing: false, loading: false, listened: false, audioError: false, audio: null,
      saved: false, starting: false, failure: '', expired: false, result: null }
  },
  computed: { item() { return this.items[this.state.answers.length] } },
  mounted() { window.addEventListener('keydown', this.handlePlaybackShortcut) },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handlePlaybackShortcut)
    this.stopAudio()
  },
  methods: {
    handlePlaybackShortcut(event) {
      if (event.code !== 'Space' || event.repeat || event.isComposing || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || this.phase !== 'question') return
      if (event.target?.isContentEditable || event.target?.closest('input, textarea, select, button, a, summary, audio, video, [role="button"], [role="textbox"], [role="dialog"], [aria-modal="true"]')) return
      event.preventDefault()
      this.play()
    },
    stopAudio() {
      const audio = this.audio
      this.audio = null
      audio?.pause()
      this.playing = false
      this.loading = false
    },
    async start() {
      if (this.starting) return
      if (!this.user) { this.showLoginModal(); return }
      this.stopAudio()
      this.starting = true
      this.failure = ''
      try {
        const response = await apiPost('/api/level-test/listening/start', { recentIds: readRecentQuestions() })
        this.state = startListeningTest(response.items)
        this.items = response.items
        this.attemptToken = response.attemptToken
        rememberQuestions(this.items.map(item => item.id))
        this.saved = false
        this.result = null
        this.expired = false
        this.phase = 'question'
        this.nextItem()
      } catch {
        this.phase = 'intro'
        this.failure = this.$t('levelTest.listeningStartFailed')
      } finally { this.starting = false }
    },
    nextItem() {
      this.stopAudio()
      this.options = shuffle(this.item.options || [])
      this.plays = 0
      this.draft = ''
      this.listened = false
      this.audioError = false
    },
    play() {
      if (this.plays >= 2 || this.playing || this.loading) return
      this.stopAudio()
      const audio = new Audio(this.item.audioUrl)
      this.audio = audio
      this.loading = true
      this.audioError = false
      let counted = false
      let failed = false
      const fail = () => {
        if (this.audio !== audio || failed) return
        failed = true
        if (counted) this.plays -= 1
        this.stopAudio()
        this.audioError = true
      }
      audio.addEventListener('playing', () => {
        if (this.audio !== audio) return
        if (!counted) { this.plays += 1; counted = true }
        this.loading = false
        this.playing = true
        if (this.item.type === 'dictation') this.$nextTick(() => this.$refs.dictationInput?.focus({ preventScroll: true }))
      })
      audio.addEventListener('ended', () => {
        if (this.audio !== audio) return
        this.playing = false
        this.listened = true
      }, { once: true })
      audio.addEventListener('error', fail, { once: true })
      audio.play().catch(fail)
    },
    submitTyped(event) {
      if (event.isComposing || this.item.type !== 'dictation' || !this.draft.trim()) return
      this.advance(this.draft)
    },
    async advance(response) {
      if (this.phase !== 'question' || !this.listened || this.playing || this.loading) return
      this.stopAudio()
      this.state = recordListeningAnswer(this.state, this.items, response, this.plays)
      if (this.state.answers.length < this.items.length) { this.nextItem(); return }
      await this.assess()
    },
    async assess() {
      if (this.phase === 'scoring') return
      this.phase = 'scoring'
      this.failure = ''
      try {
        const response = await apiPost('/api/level-test/listening', { attemptToken: this.attemptToken, answers: this.state.answers, language: this.$i18n.locale.startsWith('zh') ? 'zh' : 'en' })
        this.result = response.result
        this.phase = 'result'
      } catch (error) {
        this.expired = error.code === 'LISTENING_SESSION_EXPIRED'
        this.failure = this.$t(this.expired ? 'levelTest.listeningSessionExpired' : error.code === 'LISTENING_RATE_LIMIT' ? 'levelTest.listeningRateLimit' : 'levelTest.listeningRetryHint')
        this.phase = 'assessmentError'
        return
      }
      const completedState = this.state
      try {
        if (!this.user) throw new Error('Authentication required')
        await this.saveProfile({ ...this.profile, listeningLevel: this.result.level, listeningSource: 'test' })
        this.completeOnboardingTest('listening', this.result.level)
        if (this.state === completedState) this.saved = true
      } catch { ElMessage.error(this.$t('learnerProfile.saveFailed')) }
    }
  }
}
</script>

<style scoped>
.test-range { margin: var(--space-3) 0 var(--space-5); color: var(--ink-secondary); font-size: var(--font-size-sm); line-height: 1.6; }


.listening-test { border: 1px solid var(--border-default); border-radius: var(--radius-lg); background: var(--surface-panel); }
.test-intro, .test-question { padding: var(--space-8); }
h1, p { margin: 0; }
h1 { color: var(--ink-primary); font-size: var(--font-size-2xl); font-weight: 600; line-height: 1.5; }
.muted { color: var(--ink-secondary); font-size: var(--font-size-sm); line-height: 1.6; margin-top: var(--space-2); }
.intro-text { margin: var(--space-4) 0 var(--space-6); color: var(--ink-secondary); line-height: 1.6; }
.question-number { color: var(--ink-secondary); font-family: var(--font-family-data); font-size: var(--font-size-sm); }
progress { display: block; width: 100%; height: 4px; margin-top: var(--space-3); accent-color: var(--signal-active-strong); }
.question-text { margin-top: var(--space-6); font-size: var(--font-size-xl); }
.player { display: flex; flex-direction: column; align-items: center; margin: var(--space-6) 0; }
.play-button { display: inline-flex; align-items: center; justify-content: center; width: 64px; height: 64px; border: none; border-radius: 50%; background: var(--ink-primary); color: var(--color-text-inverse); cursor: pointer; }
.play-button .material-icons { font-size: 32px; }
.options { display: grid; gap: var(--space-3); }
.option { display: flex; align-items: center; gap: var(--space-3); width: 100%; min-height: 52px; padding: var(--space-3) var(--space-4); border: 1px solid var(--border-default); border-radius: var(--radius-md); background: var(--surface-panel); color: var(--ink-primary); font: inherit; line-height: 1.5; text-align: left; cursor: pointer; }
.option.selected { background: var(--surface-active); border-color: var(--border-active); }
.option-letter { font-family: var(--font-family-data); color: var(--ink-secondary); }
.question-actions { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--space-3); margin-top: var(--space-6); }
.primary-button, .text-button { min-height: 44px; padding: var(--space-2) var(--space-4); border: 1px solid var(--ink-primary); border-radius: var(--radius-md); background: var(--ink-primary); color: var(--color-text-inverse); font: inherit; font-size: var(--font-size-sm); cursor: pointer; }
.text-button { border-color: transparent; background: transparent; color: var(--ink-secondary); }
button:disabled { background: var(--surface-subtle); color: var(--ink-tertiary); border-color: var(--border-subtle); cursor: not-allowed; }
button:focus-visible { outline: 2px solid var(--signal-active-strong); outline-offset: 2px; }
.audio-error { margin-top: var(--space-2); color: var(--color-danger); font-size: var(--font-size-sm); }
@media (max-width: 640px) { .test-intro, .test-question { padding: var(--space-6) var(--space-4); } .question-text { font-size: var(--font-size-lg); } }

.dictation-editor { display: grid; gap: var(--space-2); }
.dictation-editor label { color: var(--ink-primary); font-size: var(--font-size-sm); font-weight: 600; }
textarea { box-sizing: border-box; width: 100%; min-height: 148px; resize: vertical; padding: var(--space-4); border: 1px solid var(--border-default); border-radius: var(--radius-md); background: var(--surface-panel); color: var(--ink-primary); font: inherit; font-size: var(--font-size-lg); line-height: 1.65; }
textarea:focus-visible { outline: 2px solid var(--signal-active-strong); outline-offset: 2px; }
textarea:disabled { background: var(--surface-subtle); color: var(--ink-tertiary); cursor: not-allowed; }
.editor-footer { display: flex; justify-content: space-between; flex-wrap: wrap; gap: var(--space-2); color: var(--ink-secondary); font-size: var(--font-size-xs); }
.result-metrics { display: flex; justify-content: center; gap: var(--space-8); margin: 0 0 var(--space-4); }
.result-metrics dt { color: var(--ink-secondary); font-size: var(--font-size-sm); }
.result-metrics dd { margin: var(--space-2) 0 0; color: var(--ink-primary); font-family: var(--font-family-data); font-size: var(--font-size-xl); }
</style>
